"use client";

import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useEffect, useRef, useState, type RefObject } from "react";
import {
  createTurnstileTokenStore,
  type TurnstileTokenStore,
} from "../lib/turnstileTokenStore";

export { TURNSTILE_FAILED_MESSAGE } from "../lib/turnstileTokenStore";

// Set once Supabase's "Bot and abuse protection" is on. Absent (or empty) the
// whole hook is a no-op: no widget, no token, every auth call unchanged.
const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || undefined;

/**
 * Cloudflare Turnstile for the Supabase auth calls that need a `captchaToken`
 * (signUp, signInWithPassword, signInWithOtp, resetPasswordForEmail).
 *
 * Render `widget` right above the form's submit button — it stays invisible
 * unless Cloudflare needs a click. In the submit handler, take
 * `await getToken()` before the Supabase call and `reset()` in a `finally`:
 * tokens are single-use, so every attempt, success or failure, must leave a
 * fresh challenge behind for the next one.
 */
export function useTurnstile() {
  const instanceRef = useRef<TurnstileInstance | null>(null);
  const [store] = useState(createTurnstileTokenStore);

  const getToken = (): Promise<string | undefined> =>
    siteKey ? store.getToken() : Promise.resolve(undefined);

  const reset = () => {
    if (!siteKey) return;
    store.clearToken();
    instanceRef.current?.reset();
  };

  const widget = siteKey ? (
    <TurnstileWidget
      siteKey={siteKey}
      store={store}
      instanceRef={instanceRef}
    />
  ) : null;

  return { enabled: siteKey !== undefined, widget, getToken, reset };
}

function TurnstileWidget({
  siteKey,
  store,
  instanceRef,
}: {
  siteKey: string;
  store: TurnstileTokenStore;
  instanceRef: RefObject<TurnstileInstance | null>;
}) {
  // A token belongs to the widget that produced it: when the widget goes
  // (the auth view switched), the token goes with it.
  useEffect(() => () => store.clearToken(), [store]);

  return (
    <Turnstile
      ref={instanceRef}
      siteKey={siteKey}
      options={{
        theme: "light",
        appearance: "interaction-only",
        size: "flexible",
      }}
      onSuccess={(token) => store.setToken(token)}
      onExpire={() => {
        store.clearToken();
        instanceRef.current?.reset();
      }}
      // The widget retries on its own (`retry: "auto"`); resetting here too
      // would loop on a persistent error, so only drop the dead token.
      onError={() => store.clearToken()}
    />
  );
}
